import type { CreateEnvVar, EnvVarType } from '@stratumn/studio-shared';
import { createEnvVarSchema, envVarValueSchema } from '@stratumn/studio-shared';
import { Hash, Loader2, Plus, ToggleLeft, Trash2, Type } from 'lucide-react';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  EnvVarCellCenter,
  EnvVarCellInput,
  EnvVarTable
} from '@/components/env-var/EnvVarTable';
import { EnvVarValueCell } from '@/components/env-var/EnvVarValueCell';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import useStores from '@/hooks/useStore';
import { extractErrorMessage } from '@/services/errors/errors';

type Props = {
  processId: string;
};

type DraftEnvVar = {
  id: string | null;
  name: string;
  type: EnvVarType;
  value: string;
  shared: boolean;
  enabled: boolean;
};

const ENV_VAR_TYPE_CONFIG: Record<
  EnvVarType,
  { label: string; icon: React.ReactNode }
> = {
  string: { label: 'String', icon: <Type className="size-3.5" /> },
  number: { label: 'Number', icon: <Hash className="size-3.5" /> },
  boolean: { label: 'Boolean', icon: <ToggleLeft className="size-3.5" /> }
};

const DEFAULT_VALUES: Record<EnvVarType, string> = {
  string: '',
  number: '0',
  boolean: 'false'
};

const createEmptyDraft = (): DraftEnvVar => ({
  id: null,
  name: '',
  type: 'boolean',
  value: 'false',
  shared: true,
  enabled: true
});

export const EnvVarsCard = observer(({ processId }: Props) => {
  const { envVarStore } = useStores();
  const [drafts, setDrafts] = useState<DraftEnvVar[]>([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

  useEffect(() => {
    envVarStore.fetchEnvVars(processId).catch(() => {
      // error already reported via newError → Sentry; suppress unhandled rejection
    });
  }, [envVarStore, processId]);

  useEffect(() => {
    if (dirty) {
      return;
    }
    setDrafts(
      envVarStore.envVars.map(v => ({
        id: v.id,
        name: v.name,
        type: v.type,
        value: v.value,
        shared: v.shared,
        enabled: v.enabled
      }))
    );
  }, [envVarStore.envVars, dirty]);

  const updateDraft = useCallback(
    (index: number, patch: Partial<DraftEnvVar>) => {
      setDrafts(prev => {
        const next = [...prev];
        next[index] = { ...next[index], ...patch };
        return next;
      });
      setDirty(true);
    },
    []
  );

  const addVariable = useCallback(() => {
    setDrafts(prev => [...prev, createEmptyDraft()]);
    setDirty(true);
  }, []);

  const removeDraft = useCallback(
    async (index: number) => {
      const draft = drafts[index];
      if (draft.id) {
        try {
          await envVarStore.deleteEnvVar(processId, draft.id);
          toast.success(`Deleted "${draft.name}"`);
        } catch (error: unknown) {
          toast.error(
            `Failed to delete "${draft.name}": ${extractErrorMessage(error)}`
          );
          return;
        }
        // Server-side delete already succeeded and the store has been
        // updated. Do NOT mark `dirty=true`: that would block the reset
        // effect from re-syncing canonical store state.
        setDeleteIndex(null);
        return;
      }
      // Removing an unsaved row: drop it from local drafts only.
      setDrafts(prev => prev.filter((_, i) => i !== index));
      setDirty(true);
      setDeleteIndex(null);
    },
    [drafts, envVarStore, processId]
  );

  const validate = useCallback((): boolean => {
    const seenNames = new Set<string>();
    for (const draft of drafts) {
      const parsed = createEnvVarSchema.safeParse({
        name: draft.name,
        type: draft.type,
        value: draft.value,
        shared: draft.shared,
        enabled: draft.enabled
      });
      if (!parsed.success) {
        const message = parsed.error.issues[0]?.message ?? 'Invalid input';
        const label = draft.name ? `"${draft.name}": ` : '';
        toast.error(`${label}${message}`);
        return false;
      }
      const valueParsed = envVarValueSchema(draft.type).safeParse(draft.value);
      if (!valueParsed.success) {
        const message = valueParsed.error.issues[0]?.message ?? 'Invalid value';
        toast.error(`"${draft.name}": ${message}`);
        return false;
      }
      if (seenNames.has(draft.name)) {
        toast.error(`Duplicate variable name "${draft.name}"`);
        return false;
      }
      seenNames.add(draft.name);
    }
    return true;
  }, [drafts]);

  const handleSave = useCallback(async () => {
    if (!validate()) {
      return;
    }

    setSaving(true);
    let savedCount = 0;
    let totalToSave = 0;
    try {
      // Count how many drafts represent a real diff with the server so the
      // partial-failure message can be accurate.
      for (const draft of drafts) {
        if (draft.id) {
          const original = envVarStore.envVars.find(v => v.id === draft.id);
          if (
            original &&
            (original.name !== draft.name ||
              original.type !== draft.type ||
              original.value !== draft.value ||
              original.shared !== draft.shared ||
              original.enabled !== draft.enabled)
          ) {
            totalToSave++;
            await envVarStore.updateEnvVar(processId, draft.id, {
              name: draft.name,
              type: draft.type,
              value: draft.value,
              shared: draft.shared,
              enabled: draft.enabled
            });
            savedCount++;
          }
        } else {
          totalToSave++;
          const dto: CreateEnvVar = {
            name: draft.name,
            type: draft.type,
            value: draft.value,
            shared: draft.shared,
            enabled: draft.enabled
          };
          await envVarStore.createEnvVar(processId, dto);
          savedCount++;
        }
      }
      toast.success('Environment variables saved');
      setDirty(false);
    } catch (error: unknown) {
      // Partial failure: some rows may have been persisted server-side
      // before the loop threw. Refetch the canonical state and clear
      // dirty so the reset effect rebuilds drafts from the server,
      // surfacing the actually-persisted state to the user.
      const message = extractErrorMessage(error);
      if (savedCount > 0) {
        toast.error(
          `Saved ${savedCount} of ${totalToSave}. ${message}. Refreshed from server.`
        );
      } else {
        toast.error(`Failed to save: ${message}`);
      }
      try {
        await envVarStore.fetchEnvVars(processId);
      } catch {
        // best-effort refresh; original error already surfaced
      }
      setDirty(false);
    } finally {
      setSaving(false);
    }
  }, [drafts, envVarStore, processId, validate]);

  const getDeleteKey = (draft: DraftEnvVar, index: number) =>
    draft.id ?? `new-${index}`;

  return (
    <div className="rounded-xl bg-card p-6 shadow-verail">
      <h3 className="mb-1 font-medium text-lg">Environment Variables</h3>
      <p className="mb-4 text-muted-foreground text-sm">
        Define environment variables available to workspaces in this process.
      </p>

      {envVarStore.loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {drafts.length > 0 && (
            <EnvVarTable
              headers={[
                { label: 'Name' },
                { label: 'Type' },
                { label: 'Default Value' },
                { label: 'Shared', center: true },
                { label: 'Enabled', center: true },
                { label: 'Actions', center: true }
              ]}
            >
              {drafts.map((draft, index) => {
                const key = getDeleteKey(draft, index);
                return (
                  <tr key={key}>
                    <EnvVarCellInput>
                      <Input
                        className="h-10 w-full rounded-none border-0 font-mono shadow-none focus-visible:ring-0"
                        onChange={e =>
                          updateDraft(index, {
                            name: e.target.value.toUpperCase()
                          })
                        }
                        placeholder="MY_VARIABLE"
                        value={draft.name}
                      />
                    </EnvVarCellInput>
                    <EnvVarCellInput>
                      <Select
                        onValueChange={(v: EnvVarType) => {
                          updateDraft(index, {
                            type: v,
                            value: DEFAULT_VALUES[v]
                          });
                        }}
                        value={draft.type}
                      >
                        <SelectTrigger className="h-10 w-full rounded-none border-0 shadow-none focus:ring-0">
                          <SelectValue>
                            <span className="flex items-center gap-1.5">
                              {ENV_VAR_TYPE_CONFIG[draft.type].icon}
                              {ENV_VAR_TYPE_CONFIG[draft.type].label}
                            </span>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {(['string', 'number', 'boolean'] as const).map(t => (
                            <SelectItem key={t} value={t}>
                              <span className="flex items-center gap-2">
                                {ENV_VAR_TYPE_CONFIG[t].icon}
                                {ENV_VAR_TYPE_CONFIG[t].label}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </EnvVarCellInput>
                    <EnvVarCellInput>
                      <EnvVarValueCell
                        onChange={v => updateDraft(index, { value: v })}
                        type={draft.type}
                        value={draft.value}
                      />
                    </EnvVarCellInput>
                    <EnvVarCellCenter>
                      <Switch
                        checked={draft.shared}
                        onCheckedChange={v => updateDraft(index, { shared: v })}
                      />
                    </EnvVarCellCenter>
                    <EnvVarCellCenter>
                      <Switch
                        checked={draft.enabled}
                        onCheckedChange={v =>
                          updateDraft(index, { enabled: v })
                        }
                      />
                    </EnvVarCellCenter>
                    <EnvVarCellCenter>
                      <Button
                        onClick={() => setDeleteIndex(index)}
                        size="icon"
                        variant="ghost"
                      >
                        <Trash2 className="size-4 text-destructive" />
                      </Button>
                    </EnvVarCellCenter>
                  </tr>
                );
              })}
            </EnvVarTable>
          )}

          {drafts.length === 0 && (
            <p className="py-4 text-center text-muted-foreground text-sm">
              No environment variables defined yet.
            </p>
          )}

          <div className="mt-4 flex items-center justify-between">
            <Button
              className="gap-1"
              onClick={addVariable}
              size="sm"
              variant="outline"
            >
              <Plus className="size-4" />
              Add variable
            </Button>
            <Button
              disabled={!dirty || saving}
              onClick={() => void handleSave()}
              size="sm"
            >
              {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
              Save
            </Button>
          </div>
        </>
      )}

      <Dialog
        onOpenChange={() => setDeleteIndex(null)}
        open={deleteIndex !== null}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete variable</DialogTitle>
            <DialogDescription>
              {deleteIndex !== null && drafts[deleteIndex] && (
                <>
                  Are you sure you want to delete{' '}
                  <span className="font-mono font-semibold">
                    {drafts[deleteIndex].name || 'this variable'}
                  </span>
                  ? All workspace overrides for this variable will be removed.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setDeleteIndex(null)} variant="ghost">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (deleteIndex !== null) {
                  void removeDraft(deleteIndex);
                }
              }}
              variant="destructive"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});
